// The Projects backend. Same shape as the Standards Library client next door — one request helper,
// typed errors, nothing clever.

import { VerityApiError } from "../StandardsLibrary/api";
import {
  AuditEvent,
  ComplianceRun,
  FindingStatus,
  ProjectDeliverable,
  ProjectDraft,
  Reviewer,
  VerityProject,
} from "./types";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/verity`;

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // `...init` must come BEFORE headers, or its own `headers` key replaces the merged object outright.
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  });

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    const detail = await res
      .json()
      .then((body) => (body as { error?: string }).error)
      .catch(() => undefined);
    throw new VerityApiError(detail || `Request failed (${res.status})`, res.status);
  }

  return (await res.json()) as T;
}

/**
 * The org's projects, or just the caller's own.
 *
 * `mine` is what the REVIEWER screen asks for, and the server honours it for administrators too —
 * the two shells share this route, so without it an administrator opening the reviewer screen saw
 * every project in the org. Narrowing is the client's to ask for; widening is the token's to grant.
 */
export async function listProjects(scope?: "mine"): Promise<VerityProject[]> {
  const { items } = await request<{ items: VerityProject[] }>(
    scope === "mine" ? "/projects?scope=mine" : "/projects",
  );
  return items || [];
}

/**
 * One project, with whatever compliance run is stored on it.
 *
 * Used by a screen that reattached to a check it did not start: the run's own request belongs to a
 * page that has gone, so the result is collected from the record instead of from the response.
 */
export async function getProject(id: string): Promise<VerityProject> {
  const { project } = await request<{ project: VerityProject }>(`/projects/${id}`);
  return project;
}

/**
 * Who can be assigned a project — the org's approved members.
 *
 * Auth0-backed, so it costs a round trip: called when the Add Project dialog opens, never for a row.
 */
export async function listReviewers(): Promise<Reviewer[]> {
  const { items } = await request<{ items: Reviewer[] }>("/reviewers");
  return items || [];
}

export async function createProject(draft: ProjectDraft): Promise<VerityProject> {
  const { project } = await request<{ project: VerityProject }>("/projects", {
    method: "POST",
    body: JSON.stringify(draft),
  });
  return project;
}

export async function updateProject(id: string, draft: ProjectDraft): Promise<VerityProject> {
  const { project } = await request<{ project: VerityProject }>(`/projects/${id}`, {
    method: "PUT",
    body: JSON.stringify(draft),
  });
  return project;
}

/** Removes the project, its deliverable records, and the submitted files behind them. */
export async function deleteProject(id: string): Promise<void> {
  await request<void>(`/projects/${id}`, { method: "DELETE" });
}

export async function listDeliverables(projectId: string): Promise<ProjectDeliverable[]> {
  const { items } = await request<{ items: ProjectDeliverable[] }>(
    `/projects/${projectId}/deliverables`,
  );
  return items || [];
}

/** Submits one or more files against a project. Returns the rows that were written. */
/** How far a submission has got in the half the browser cannot watch. 204 → null. */
export async function uploadProgress(
  projectId: string,
  uploadId: string,
): Promise<{ loaded: number; total: number; done: number; count: number } | null> {
  const res = await fetch(
    `${BASE}/projects/${projectId}/deliverables/upload-progress?upload=${encodeURIComponent(uploadId)}`,
    { credentials: "include" },
  );
  if (res.status === 204 || !res.ok) return null;
  const { progress } = (await res.json()) as { progress: { loaded: number; total: number; done: number; count: number } };
  return progress ?? null;
}

export interface ParseProgressReport {
  stage: "fetching" | "reading" | "saving" | "done";
  detail: string;
  fraction: number;
  etaSeconds: number | null;
}

/** How far Document AI has got with one deliverable. 204 → null, meaning "nothing to say". */
export async function parseProgress(
  projectId: string,
  deliverableId: string,
): Promise<ParseProgressReport | null> {
  const res = await fetch(
    `${BASE}/projects/${projectId}/deliverables/${deliverableId}/parse-progress`,
    { credentials: "include" },
  );
  if (res.status === 204 || !res.ok) return null;
  const { progress } = (await res.json()) as { progress: ParseProgressReport };
  return progress ?? null;
}

export async function uploadDeliverables(
  projectId: string,
  files: File[],
  onProgress?: (fraction: number) => void,
  /** Names this submission so its storage write can be polled while the POST is still open. */
  uploadId?: string,
): Promise<ProjectDeliverable[]> {
  const form = new FormData();
  for (const file of files) form.append("files", file);

  // XMLHttpRequest, NOT fetch, and only for this reason: fetch cannot report how much of a REQUEST
  // body has gone. Response progress it can stream; request progress it cannot, and the streaming
  // upload that would fix that is not available across the browsers this has to run in. `xhr.upload`
  // has reported this since forever. Everything else here matches request() above — credentials,
  // the server's own `error` text, and a VerityApiError carrying the status.
  //
  // No Content-Type is set: the browser has to choose the multipart boundary itself.
  return new Promise<ProjectDeliverable[]>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(
      "POST",
      `${BASE}/projects/${projectId}/deliverables${uploadId ? `?upload=${encodeURIComponent(uploadId)}` : ""}`,
    );
    xhr.withCredentials = true;

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable || e.total <= 0) return;
        onProgress(Math.min(1, e.loaded / e.total));
      };
      // THE LAST BYTE SENT IS NOT THE JOB DONE. The server still has to write every file to blob
      // storage and answer, and on a 35 MB pack that tail is seconds. Reporting 1 here lets the
      // caller stop counting and say what is actually happening instead of parking a bar at 99%.
      xhr.upload.onload = () => onProgress(1);
    }

    xhr.onload = () => {
      let body: { items?: ProjectDeliverable[]; error?: string } | undefined;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        // A non-JSON body is a proxy or a crash, and the status is the only thing worth reporting.
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body?.items ?? []);
        return;
      }
      reject(new VerityApiError(body?.error || `Upload failed (${xhr.status})`, xhr.status));
    };

    // Status 0 throughout: there is no response to take a status from, which is the difference
    // between "the server refused this" and "the server was never reached".
    xhr.onerror = () =>
      reject(new VerityApiError("The upload could not reach the server.", 0));
    xhr.ontimeout = () => reject(new VerityApiError("The upload timed out.", 0));
    xhr.onabort = () => reject(new VerityApiError("The upload was cancelled.", 0));

    xhr.send(form);
  });
}

/**
 * Runs Document AI over one deliverable.
 *
 * A PDF drawing pack is read for real: both text sources merged, sheets identified from their title
 * blocks, notes split, standards harvested. Anything else comes back as "failed" with the reason in
 * `analysisError`, because there is no reader for it yet.
 */
export async function parseDeliverable(
  projectId: string,
  deliverableId: string,
): Promise<ProjectDeliverable> {
  const { deliverable } = await request<{ deliverable: ProjectDeliverable }>(
    `/projects/${projectId}/deliverables/${deliverableId}/parse`,
    { method: "POST" },
  );
  return deliverable;
}

export async function deleteDeliverable(projectId: string, deliverableId: string): Promise<void> {
  await request<void>(`/projects/${projectId}/deliverables/${deliverableId}`, { method: "DELETE" });
}

/** The submitted file, streamed by the backend under the caller's own permission. */
export function deliverableFileUrl(projectId: string, deliverableId: string): string {
  return `${BASE}/projects/${projectId}/deliverables/${deliverableId}/file`;
}

/**
 * Runs the compliance engine over the selected deliverables and standards.
 *
 * Long — a whole standard is hundreds of model calls — so the screen polls complianceProgress()
 * alongside this rather than leaving a spinner to stand in for ten minutes of silence.
 */
export async function runCompliance(
  projectId: string,
  deliverableIds: string[],
  standardIds: string[],
): Promise<ComplianceRun> {
  const { run } = await request<{ run: ComplianceRun }>(`/projects/${projectId}/compliance`, {
    method: "POST",
    body: JSON.stringify({ deliverableIds, standardIds }),
  });
  return run;
}

/**
 * Downloads the run as a spreadsheet or a PDF.
 *
 * Fetched rather than linked, because the route needs the session cookie and an `<a download>`
 * would send an unauthenticated request. The blob is handed to a temporary link, which is the only
 * way to give a browser a file it did not navigate to.
 */
export async function exportFindings(projectId: string, format: "xlsx" | "pdf"): Promise<void> {
  const res = await fetch(`${BASE}/projects/${projectId}/findings/export?format=${format}`, {
    credentials: "include",
  });
  if (!res.ok) {
    const detail = await res
      .json()
      .then((body) => (body as { error?: string }).error)
      .catch(() => undefined);
    throw new VerityApiError(detail || `Export failed (${res.status})`, res.status);
  }

  // The server names the file; parsing it back out of the header keeps one source of that name.
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const named = /filename="([^"]+)"/.exec(disposition)?.[1];

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = named || `findings.${format}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Released on the next tick — revoking synchronously races the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Accepts, dismisses or reopens one finding. Returns the whole run with the decision applied. */
export async function decideFinding(
  projectId: string,
  findingId: string,
  status: FindingStatus,
  note?: string,
): Promise<ComplianceRun> {
  const { run } = await request<{ run: ComplianceRun }>(
    `/projects/${projectId}/findings/${findingId}`,
    { method: "PATCH", body: JSON.stringify({ status, note }) },
  );
  return run;
}

/** Where a running compliance check has got to. Null when nothing is running. */
export interface RunProgress {
  stage: "planning" | "reading" | "checking" | "done";
  /**
   * Which stage this is, and how many there are.
   *
   * Counted by the engine, not assumed here: a run with no threshold rules never reads declared
   * values, so the number of stages varies and hard-coding it would be wrong on the runs where
   * nothing went wrong.
   */
  stageIndex?: number;
  stageCount?: number;
  pack?: string;
  rulesDone: number;
  rulesTotal: number;
  findings: number;
  detail: string;
  /** What has happened so far, oldest first. Only lines worth keeping — see the engine. */
  steps: string[];
  /** What the run was told to check, so a screen reattaching to it can restore the ticks. */
  deliverableIds?: string[];
  standardIds?: string[];
  startedAt: string;
  stageStartedAt: string;
  updatedAt: string;
  /** Seconds left, projected from the work so far. Null until enough has been done to mean anything. */
  etaSeconds: number | null;
  /** How a finished run ended. Absent while it is still going. */
  outcome?: "complete" | "stopped";
}

/**
 * Polled while a check runs.
 *
 * Returns null both when nothing is running and when the poll fails — a progress bar is a courtesy,
 * and an error from it must never surface as though the run itself had failed.
 */
export async function complianceProgress(projectId: string): Promise<RunProgress | null> {
  try {
    const res = await request<{ progress?: RunProgress } | null>(
      `/projects/${projectId}/compliance/progress`,
      { method: "GET" },
    );
    return res?.progress ?? null;
  } catch {
    return null;
  }
}

/**
 * Asks a running check to stop.
 *
 * Advisory: the engine stops between batches, so a call already in flight still completes. Returns
 * quietly on failure — a stop that could not be delivered must not look like the run itself broke.
 */
export async function stopCompliance(projectId: string): Promise<void> {
  try {
    await request<void>(`/projects/${projectId}/compliance/stop`, { method: "POST" });
  } catch {
    /* the run will end on its own; nothing useful to say here */
  }
}

/**
 * Everything that has happened to a project, newest first.
 *
 * `available` is whether the log STORE exists, which the panel needs separately from whether the
 * list is empty — an environment that has not been provisioned and a project where nothing has
 * happened otherwise look identical.
 */
export async function listAudit(
  projectId: string,
): Promise<{ items: AuditEvent[]; available: boolean }> {
  const body = await request<{ items: AuditEvent[]; available?: boolean }>(
    `/projects/${projectId}/audit`,
  );
  return { items: body.items || [], available: body.available !== false };
}
